import { canonicalUrl } from "@/config/canonical";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { useGoBack } from "@/hooks/useGoBack";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import {
  evolvedPlayersSelector,
  fetchSavedEvolutionPlayers,
  getMyEvolutionPlayers,
} from "@repo/shared/redux";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useMatch } from "react-router";

import { EvolutionsHubCards } from "../EvolutionsHub";
import { EvolutionsMobileNav } from "../EvolutionsMobileNav";

export const MyEvolvedPlayersLayout = () => {
  const goBack = useGoBack();
  const isSaved = !!useMatch("/evolutions/my-evolutions/saved");
  const seoCopy = useSeoCopy(
    isSaved ? "myEvolutionsSaved" : "myEvolutionsPlayers",
  );
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const {
    savedEvolutionPlayersStatus,
    myEvolvedPlayersStatus,
    savedEvolutionPlayers,
    myEvolvedPlayers,
  } = useSelector(evolvedPlayersSelector);

  const prevSavedCount = useRef<number | null>(null);
  const prevEvolvedCount = useRef<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initial load — always fetch fresh on mount
  useEffect(() => {
    if (!isLoggedIn) return;
    dispatch(fetchSavedEvolutionPlayers());
    dispatch(
      getMyEvolutionPlayers({ sort: "meta-rating", playerRoleId: undefined }),
    );
  }, [isLoggedIn]);

  // Cross-fetch: when saved count changes, refresh evolved list
  useEffect(() => {
    const count = savedEvolutionPlayers?.length ?? 0;
    if (
      prevSavedCount.current !== null &&
      count !== prevSavedCount.current &&
      isLoggedIn
    ) {
      dispatch(
        getMyEvolutionPlayers({ sort: "meta-rating", playerRoleId: undefined }),
      );
    }
    prevSavedCount.current = count;
  }, [savedEvolutionPlayers?.length]);

  // Cross-fetch: when evolved count changes, refresh saved list
  useEffect(() => {
    const count = myEvolvedPlayers?.length ?? 0;
    if (
      prevEvolvedCount.current !== null &&
      count !== prevEvolvedCount.current &&
      isLoggedIn
    ) {
      dispatch(fetchSavedEvolutionPlayers());
    }
    prevEvolvedCount.current = count;
  }, [myEvolvedPlayers?.length]);

  return (
    <div className="min-h-screen mx-auto bg-background">
      <HeroSection
        helmetTitle={seoCopy.title}
        pageTitle={seoCopy.pageTitle}
        helmetDescription={seoCopy.description}
        subtitle={seoCopy.subtitle}
        helmetCanonicalLink={canonicalUrl(
          `/evolutions/my-evolutions/${isSaved ? "saved" : "players"}`,
        )}
        back={goBack}
        shouldBeInTheDocumentFlow
      >
        <div className="my-8 hidden mobile:block">
          <EvolutionsHubCards activeEvoCard="my-evolutions" />
        </div>
      </HeroSection>
      <EvolutionsMobileNav active="my-evolutions" />
      <Outlet />
    </div>
  );
};
